import { useCallback, useEffect, useReducer, useRef } from 'react';
import { gameReducer, initialGameState, toGameRecord, type GameState } from '../game/gameState';
import { LocationQueue, prefetchPanoramaImage } from '../game/locationQueue';
import { getGameMap } from '../game/maps';
import { ROUNDS_PER_GAME } from '../game/scoring';
import type { GameSettings } from '../game/settings';
import { loadStats, recordGame, saveStats, type PlayerStats } from '../game/stats';
import { fallbackProviders, getProvider } from '../providers';
import { onGoogleAuthFailure } from '../providers/googleMaps';
import { isAbortError } from '../providers/search';
import { NoPanoramaFoundError, ProviderUnavailableError, type ProviderId } from '../providers/types';

/** After this many panoramas that fail to display in a row, the source is considered broken. */
const MAX_DISPLAY_FAILURES = 3;

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useGame(initialSettings: GameSettings, onStatsChange: (stats: PlayerStats) => void) {
  const [state, dispatch] = useReducer(gameReducer, initialSettings, initialGameState);
  const stateRef = useRef<GameState>(state);
  stateRef.current = state;
  const queueRef = useRef<LocationQueue | null>(null);
  const displayFailuresRef = useRef(0);
  const recordedGameRef = useRef<number | null>(null);

  const disposeQueue = useCallback(() => {
    queueRef.current?.dispose();
    queueRef.current = null;
  }, []);

  const createQueue = useCallback((providerId: ProviderId) => {
    queueRef.current?.dispose();
    const s = stateRef.current;
    queueRef.current = new LocationQueue(
      getProvider(providerId),
      getGameMap(s.settings.mapId),
      ROUNDS_PER_GAME,
      () => stateRef.current.rounds.map((r) => r.panorama.position),
      (panorama) => {
        if (panorama.imageUrls.length > 0) prefetchPanoramaImage(panorama);
      },
    );
    return queueRef.current;
  }, []);

  /** Switches to the next usable source, or returns false if there is none. */
  const switchProvider = useCallback(
    (failed: ProviderId, reason: string): boolean => {
      const s = stateRef.current;
      const next = fallbackProviders(failed, s.settings.mapId)[0];
      if (!next) return false;
      createQueue(next.id).discardFrom(s.roundIndex);
      displayFailuresRef.current = 0;
      dispatch({
        type: 'provider-switched',
        providerId: next.id,
        notice: `${reason} La partie continue avec ${next.name}.`,
      });
      return true;
    },
    [createQueue],
  );

  // Load the location of the current round whenever we enter the "loading" phase.
  useEffect(() => {
    if (state.phase !== 'loading') return;
    const gameId = state.gameId;
    const roundIndex = state.roundIndex;
    let cancelled = false;
    const queue =
      queueRef.current && queueRef.current.provider.id === state.providerId ? queueRef.current : createQueue(state.providerId);

    queue
      .get(roundIndex)
      .then((panorama) => {
        if (cancelled || stateRef.current.gameId !== gameId) return;
        dispatch({ type: 'round-loaded', panorama });
      })
      .catch((error: unknown) => {
        if (cancelled || stateRef.current.gameId !== gameId || isAbortError(error)) return;
        queueRef.current?.discardFrom(roundIndex);
        const providerId = stateRef.current.providerId;
        if (error instanceof ProviderUnavailableError) {
          if (switchProvider(providerId, error.message)) return;
          dispatch({ type: 'load-failed', error: { message: error.message, retryable: true } });
          return;
        }
        if (error instanceof NoPanoramaFoundError) {
          dispatch({ type: 'load-failed', error: { message: error.message, retryable: true } });
          return;
        }
        dispatch({
          type: 'load-failed',
          error: { message: `Erreur inattendue : ${(error as Error)?.message ?? String(error)}`, retryable: true },
        });
      });
    return () => {
      cancelled = true;
    };
    // `state.providerId` changes trigger a reload with the new source.
  }, [state.phase, state.gameId, state.roundIndex, state.providerId, createQueue, switchProvider]);

  // Google rejects the key asynchronously (gm_authFailure): fall back if the current round uses Google.
  useEffect(
    () =>
      onGoogleAuthFailure(() => {
        const s = stateRef.current;
        const current = s.rounds[s.roundIndex];
        const usingGoogle = s.providerId === 'google' || current?.panorama.provider === 'google';
        if (!usingGoogle || s.phase === 'home' || s.phase === 'summary') return;
        const reason = 'La clé Google Maps a été refusée.';
        if (s.phase === 'playing') {
          if (switchProvider('google', reason)) dispatch({ type: 'reload-round' });
          else dispatch({ type: 'reload-round', notice: reason });
        } else if (s.phase === 'loading') {
          switchProvider('google', reason);
        }
      }),
    [switchProvider],
  );

  // Save statistics once per finished game.
  useEffect(() => {
    if (state.phase !== 'summary' || recordedGameRef.current === state.gameId) return;
    recordedGameRef.current = state.gameId;
    const outcome = recordGame(loadStats(), toGameRecord(state, newId()));
    saveStats(outcome.stats);
    onStatsChange(outcome.stats);
    dispatch({ type: 'game-recorded', outcome: { isBestOverall: outcome.isBestOverall, isBestOnMap: outcome.isBestOnMap } });
  }, [state, onStatsChange]);

  useEffect(() => disposeQueue, [disposeQueue]);

  const start = useCallback(
    (settings: GameSettings) => {
      disposeQueue();
      displayFailuresRef.current = 0;
      dispatch({ type: 'start', settings });
    },
    [disposeQueue],
  );

  const goHome = useCallback(() => {
    disposeQueue();
    dispatch({ type: 'home' });
  }, [disposeQueue]);

  /** The panorama of the current round was displayed correctly. */
  const panoramaDisplayed = useCallback(() => {
    displayFailuresRef.current = 0;
  }, []);

  /** The panorama of the current round could not be displayed (image blocked, deleted…). */
  const panoramaFailed = useCallback(() => {
    const s = stateRef.current;
    if (s.phase !== 'playing') return;
    displayFailuresRef.current += 1;
    queueRef.current?.discardFrom(s.roundIndex);
    if (displayFailuresRef.current >= MAX_DISPLAY_FAILURES) {
      const provider = getProvider(s.providerId);
      if (switchProvider(s.providerId, `Les images ${provider.name} ne s’affichent pas.`)) {
        dispatch({ type: 'reload-round' });
        return;
      }
      displayFailuresRef.current = 0;
    }
    dispatch({ type: 'reload-round', notice: 'Ce panorama n’a pas pu être affiché, un autre lieu a été choisi.' });
  }, [switchProvider]);

  const retry = useCallback(() => {
    queueRef.current?.discardFrom(stateRef.current.roundIndex);
    dispatch({ type: 'retry' });
  }, []);

  /** Retry the current round with another source chosen by the player. */
  const switchTo = useCallback(
    (providerId: ProviderId) => {
      createQueue(providerId).discardFrom(stateRef.current.roundIndex);
      dispatch({ type: 'provider-switched', providerId, notice: `Source d’images : ${getProvider(providerId).name}.` });
      dispatch({ type: 'retry' });
    },
    [createQueue],
  );

  return {
    state,
    dispatch,
    start,
    goHome,
    retry,
    switchTo,
    panoramaDisplayed,
    panoramaFailed,
  };
}
